import { useState } from 'react';
import BottomSheet from './BottomSheet';
import { useAcademySettings } from '../../../hooks/useAcademySettings';

const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (err) {
      console.warn('Clipboard copy failed:', err);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="min-h-[44px] rounded-full border border-stone-300 bg-white px-4 text-xs font-semibold text-stone-700 transition hover:bg-stone-50"
    >
      {copied ? 'Copied!' : label || 'Copy'}
    </button>
  );
};

const CredentialsCard = ({ open, onClose, result }) => {
  const { academy } = useAcademySettings();
  const [shared, setShared] = useState(false);

  if (!result) return null;

  const { studentName, email, password, loginUrl, academyStudentId, phone } = result;
  const academyName = academy?.name || 'Brissbella Academy';

  const shareText = [
    `Welcome to ${academyName}, ${studentName}!`,
    `Login: ${loginUrl}`,
    `Email: ${email}`,
    `Temporary password: ${password}`,
    academyStudentId ? `Student ID: ${academyStudentId}` : '',
    'Please sign in and complete your profile.',
  ]
    .filter(Boolean)
    .join('\n');

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${academyName} — your login`,
          text: shareText,
        });
        setShared(true);
      }
    } catch (err) {
      console.warn('Share canceled or failed:', err);
    }
  };

  const handleMail = () => {
    const subject = encodeURIComponent(`Welcome to ${academyName}`);
    const body = encodeURIComponent(shareText);
    window.open(`mailto:${email}?subject=${subject}&body=${body}`);
  };

  const handleSms = () => {
    if (!phone) return;
    const body = encodeURIComponent(shareText);
    window.open(`sms:${phone}?body=${body}`);
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Account created"
      description="Share these login details with the student."
      footer={
        <div className="flex items-center justify-between gap-2">
          <CopyButton value={shareText} label="Copy all" />
          <button
            type="button"
            onClick={onClose}
            className="min-h-[52px] rounded-2xl bg-[#d61f74] px-5 text-sm font-semibold text-white"
          >
            Done
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-2xl border border-[#f4b6d2] bg-[#fdf2f8] px-4 py-4 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#b91c66]">
            Welcome
          </p>
          <p className="mt-1 text-xl font-semibold text-stone-950">{studentName}</p>
          {academyStudentId ? (
            <p className="mt-1 text-sm text-stone-700">{academyStudentId}</p>
          ) : null}
        </div>

        <div className="space-y-3">
          <CredentialRow label="Login URL" value={loginUrl} />
          <CredentialRow label="Email" value={email} />
          <CredentialRow label="Temporary password" value={password} mono />
          {phone ? <CredentialRow label="Phone" value={phone} /> : null}
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <button
            type="button"
            onClick={handleShare}
            disabled={!navigator.share}
            className="min-h-[52px] rounded-2xl border border-stone-300 bg-white px-4 text-sm font-semibold text-stone-700 disabled:opacity-40"
          >
            {shared ? 'Shared' : 'Share'}
          </button>
          <button
            type="button"
            onClick={handleMail}
            className="min-h-[52px] rounded-2xl border border-stone-300 bg-white px-4 text-sm font-semibold text-stone-700"
          >
            Email
          </button>
          <button
            type="button"
            onClick={handleSms}
            disabled={!phone}
            className="col-span-2 min-h-[52px] rounded-2xl border border-stone-300 bg-white px-4 text-sm font-semibold text-stone-700 disabled:opacity-40 sm:col-span-1"
          >
            SMS
          </button>
        </div>

        <p className="text-xs text-stone-500">
          SMS and email buttons hand off to the device's messaging apps. Automated
          delivery can be wired into the Cloud Function stub later.
        </p>
      </div>
    </BottomSheet>
  );
};

const CredentialRow = ({ label, value, mono }) => (
  <div className="flex items-center justify-between gap-3 rounded-2xl border border-stone-200 bg-stone-50 px-4 py-3">
    <div className="min-w-0 flex-1">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
        {label}
      </p>
      <p
        className={`mt-0.5 truncate text-sm text-stone-950 ${
          mono ? 'font-mono text-base tracking-wider' : ''
        }`}
      >
        {value}
      </p>
    </div>
    <CopyButton value={value} />
  </div>
);

export default CredentialsCard;
